import { EnemyId } from "../state/runState";

/**
 * Theme identifier for floor visual/audio theming.
 */
export type FloorTheme =
  | "bat_cave"
  | "goblin_warren"
  | "slime_pit"
  | "spider_nest"
  | "skeleton_crypt"
  | "dark_forest"
  | "treasure_vault"
  | "dragon_lair"
  | "boss_arena";

/**
 * Floor definition interface.
 * Defines the parameters for a floor theme including enemy pools, encounter counts, and difficulty.
 */
export interface FloorDef {
  /** Unique theme identifier (e.g., "bat_cave", "goblin_warren") */
  id: FloorTheme;
  /** Display name for the floor theme */
  name: string;
  /** Enemy IDs that can spawn in encounters for this floor */
  enemyPool: EnemyId[];
  /** Min/max encounters on this floor (e.g., [3, 4]) */
  encounterCountRange: [number, number];
  /** Base difficulty multiplier for this floor (affects enemy scaling) */
  difficultyMultiplier: number;
  /** Boss ID candidate for this floor theme */
  bossCandidate: string;
}

/**
 * Floor configuration registry.
 * Maps floor theme IDs to their definitions.
 */
export const FLOOR_CONFIGS: Record<FloorTheme, FloorDef> = {
  // Floor 1 themes (introductory/easier)
  bat_cave: {
    id: "bat_cave",
    name: "Bat Cave",
    enemyPool: ["bat"],
    encounterCountRange: [3, 4],
    difficultyMultiplier: 1.0,
    bossCandidate: "boss_bat",
  },
  goblin_warren: {
    id: "goblin_warren",
    name: "Goblin Warren",
    enemyPool: ["goblin"],
    encounterCountRange: [3, 4],
    difficultyMultiplier: 1.0,
    bossCandidate: "boss_goblin",
  },
  slime_pit: {
    id: "slime_pit",
    name: "Slime Pit",
    enemyPool: ["slime"],
    encounterCountRange: [3, 4],
    difficultyMultiplier: 1.0,
    bossCandidate: "boss_slime",
  },
  // Floor 2 themes (mid-difficulty)
  spider_nest: {
    id: "spider_nest",
    name: "Spider Nest",
    enemyPool: ["spider"],
    encounterCountRange: [4, 5],
    difficultyMultiplier: 1.2,
    bossCandidate: "boss_spider",
  },
  skeleton_crypt: {
    id: "skeleton_crypt",
    name: "Skeleton Crypt",
    enemyPool: ["skeleton"],
    encounterCountRange: [4, 5],
    difficultyMultiplier: 1.2,
    bossCandidate: "boss_skeleton",
  },
  dark_forest: {
    id: "dark_forest",
    name: "Dark Forest",
    enemyPool: ["goblin", "bat", "spider"],
    encounterCountRange: [4, 5],
    difficultyMultiplier: 1.2,
    bossCandidate: "boss_treant",
  },
  // Floor 3 themes (challenging/climactic)
  treasure_vault: {
    id: "treasure_vault",
    name: "Treasure Vault",
    enemyPool: ["goblin", "skeleton", "spider"],
    encounterCountRange: [5, 6],
    difficultyMultiplier: 1.5,
    bossCandidate: "boss_mimic",
  },
  dragon_lair: {
    id: "dragon_lair",
    name: "Dragon Lair",
    enemyPool: ["skeleton", "spider", "slime"],
    encounterCountRange: [5, 6],
    difficultyMultiplier: 1.5,
    bossCandidate: "boss_dragon",
  },
  boss_arena: {
    id: "boss_arena",
    name: "Boss Arena",
    enemyPool: ["goblin", "bat", "skeleton", "spider", "slime"],
    encounterCountRange: [5, 6],
    difficultyMultiplier: 1.5,
    bossCandidate: "boss_champion",
  },
};

/**
 * Floor theme pools by floor level.
 * Each floor level (1, 2, 3) has a pool of eligible themes that can be randomly selected.
 */
export const FLOOR_THEME_POOLS: Record<number, FloorTheme[]> = {
  1: ["bat_cave", "goblin_warren", "slime_pit"],
  2: ["spider_nest", "skeleton_crypt", "dark_forest"],
  3: ["treasure_vault", "dragon_lair", "boss_arena"],
};

/**
 * Select a random floor theme from the pool for the given floor level.
 * @param floorLevel - The floor level (1, 2, or 3)
 * @param rng - Seeded random number generator
 * @returns A FloorDef from the appropriate pool
 */
export function selectFloorTheme(floorLevel: number, rng: { pick<T>(array: T[]): T }): FloorDef {
  if (!Number.isFinite(floorLevel)) {
    throw new Error(`Invalid floor level: ${floorLevel}`);
  }

  // Floors are currently capped at 3 in normal gameplay, but debug/automation may
  // request higher floors to stress scaling. Clamp to the closest supported pool.
  const normalized = Math.floor(floorLevel);
  if (normalized < 1) {
    throw new Error(`Invalid floor level: ${floorLevel}`);
  }
  const clampedFloor = Math.min(3, normalized);

  const pool = FLOOR_THEME_POOLS[clampedFloor];
  if (!pool || pool.length === 0) {
    throw new Error(`No theme pool defined for floor level ${clampedFloor}`);
  }
  const themeId = rng.pick(pool);
  const config = FLOOR_CONFIGS[themeId];
  if (!config) {
    throw new Error(`No floor config found for theme: ${themeId}`);
  }
  return config;
}
